import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ImageIcon, PlusIcon } from 'lucide-react';

import { Button } from '@/components/new-ui/button';
import { Card, CardContent } from '@/components/new-ui/card';

type MediaSectionCardProps = {
  title: string;
  icon: ReactNode;
  count: number;
  // image of the latest item, falls back to a placeholder icon
  previewSrc?: string;
  // stretched link element to the section page, rendered below the upload button
  link: ReactNode;
  // upload through a modal (writers only)
  onUpload?: () => void;
  // upload through an external page (writers only)
  uploadHref?: string;
};

// Unparsable dates sort last instead of turning every comparison into NaN
const getCreatedTime = (createdAt?: string | null) => {
  const time = new Date(createdAt ?? 0).getTime();
  return Number.isNaN(time) ? Number.NEGATIVE_INFINITY : time;
};

// Latest item by creation date, used for the overview card preview
export const getLatestItem = <T extends { createdAt?: string | null }>(items: T[]) =>
  items.reduce<T | undefined>((latest, item) => {
    if (!latest) return item;
    return getCreatedTime(item.createdAt) >= getCreatedTime(latest.createdAt) ? item : latest;
  }, undefined);

export const MediaSectionCard = ({
  title,
  icon,
  count,
  previewSrc,
  link,
  onUpload,
  uploadHref,
}: MediaSectionCardProps) => {
  const { t } = useTranslation();

  return (
    <Card className="group relative cursor-pointer transition-colors hover:border-primary/50">
      {link}
      <CardContent className="p-3">
        <div className="relative mb-3 flex aspect-square items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/50">
          {previewSrc ? (
            <img src={previewSrc} alt={title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex flex-col items-center justify-center text-muted-foreground/50">
              <ImageIcon className="mb-1 h-8 w-8" />
            </div>
          )}
          {onUpload ? (
            <Button
              size="sm"
              variant="secondary"
              className="absolute bottom-2 right-2 z-10 gap-1 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
              onClick={onUpload}
            >
              <PlusIcon className="h-3 w-3" />
              {t('uploadMedia')}
            </Button>
          ) : null}
          {uploadHref ? (
            <Button
              size="sm"
              variant="secondary"
              className="absolute bottom-2 right-2 z-10 gap-1 opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
              asChild
            >
              <a href={uploadHref} target="_blank" rel="noreferrer">
                <PlusIcon className="h-3 w-3" />
                {t('uploadMedia')}
              </a>
            </Button>
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 text-muted-foreground">{icon}</span>
            <span className="min-w-0 break-words text-sm font-medium leading-tight">{title}</span>
          </div>
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs tabular-nums text-muted-foreground">
            {count}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export const MEDIA_SECTION_LINK_CLASS = 'absolute inset-0 z-[1] rounded-lg';
